// src/pages/rapim/RapimKeputusan.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Plus, Edit, Trash2, Download,
  Target, Users, TrendingUp, Loader2,
  AlertTriangle,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Modal, Input, Select, Textarea, ProgressBar } from '../../components/ui';
import { PpeppRekomendasi, Unit, User } from '../../types';
import toast from 'react-hot-toast';
import { rapimService } from '../../services/rapim';
import { adminService } from '../../services/admin';

interface KeputusanForm {
  laporan_masalah_id: number | null;
  program: string;
  pengendalian: string;
  evaluasi: string;
  pengembangan: string;
  pelaporan: string;
  pic_unit_id: string;
  pic_user_id: string;
  target_date: string;
  budget: string;
}

const emptyForm: KeputusanForm = {
  laporan_masalah_id: null,
  program: '',
  pengendalian: '',
  evaluasi: '',
  pengembangan: '',
  pelaporan: '',
  pic_unit_id: '',
  pic_user_id: '',
  target_date: '',
  budget: '',
};

const RapimKeputusan: React.FC = () => {
  const { id } = useParams();
  const [keputusan, setKeputusan] = useState<PpeppRekomendasi[]>([]);
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState<Unit[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<KeputusanForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Unit types excluded from PIC selection — leadership/policy level, bukan tim teknis
  const EXCLUDED_UNIT_TYPES = ['UNIVERSITAS', 'FAKULTAS', 'WAREK'];
  // Roles excluded from PIC selection — kebijakan, bukan pelaksana teknis
  const EXCLUDED_PIC_ROLES = ['rektor', 'dekan', 'wakil_rektor_1', 'wakil_rektor_2', 'wakil_rektor_3', 'wakil_rektor_4', 'warek', 'warek1', 'warek2', 'warek3', 'lpm'];

  const fetchData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [kepRes, unitsRes, usersRes] = await Promise.all([
        rapimService.getKeputusanByRapat(Number(id)),
        adminService.units.getAll(),
        adminService.users.getAll(),
      ]);
      const kepData = (kepRes as any)?.data || kepRes;
      setKeputusan(Array.isArray(kepData) ? kepData : (kepData?.data || []));

      const unitsData = (unitsRes as any)?.data || unitsRes;
      const allUnits = Array.isArray(unitsData) ? unitsData : (unitsData?.data || []);
      // Filter out leadership/policy-level units
      setUnits(allUnits.filter((u: any) => !EXCLUDED_UNIT_TYPES.includes((u.type || '').toUpperCase())));

      const usersData = (usersRes as any)?.data || usersRes;
      const allUsers = Array.isArray(usersData) ? usersData : (usersData?.data || []);
      // Filter out leadership roles from PIC selection
      setUsers(allUsers.filter((u: any) => {
        const roleName = (u.role?.name || '').toLowerCase();
        return !EXCLUDED_PIC_ROLES.includes(roleName);
      }));

    } catch (e) {
      console.error(e);
      toast.error('Gagal memuat data keputusan');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const openCreate = (laporanId?: number) => {
    setEditingId(null);
    setForm({ ...emptyForm, laporan_masalah_id: laporanId || null });
    setShowModal(true);
  };

  const openEdit = (k: PpeppRekomendasi) => {
    setEditingId(k.id);
    setForm({
      laporan_masalah_id: k.laporan_masalah_id,
      program: k.program,
      pengendalian: k.pengendalian,
      evaluasi: k.evaluasi,
      pengembangan: k.pengembangan,
      pelaporan: k.pelaporan,
      pic_unit_id: k.pic_unit_id.toString(),
      pic_user_id: k.pic_user_id?.toString() || '',
      target_date: k.target_date.split('T')[0],
      budget: k.budget?.toString() || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!id) return;

    // ✅ ENHANCED: Validasi lebih ketat (Sama dengan RapimDetail)
    const errors: string[] = [];

    if (!form.program || form.program.trim().length < 5) {
      errors.push('Program minimal 5 karakter');
    }
    if (!form.pengendalian || form.pengendalian.trim().length < 5) {
      errors.push('Pengendalian minimal 5 karakter');
    }
    if (!form.evaluasi || form.evaluasi.trim().length < 5) {
      errors.push('Evaluasi minimal 5 karakter');
    }
    if (!form.pengembangan || form.pengembangan.trim().length < 5) {
      errors.push('Pengembangan minimal 5 karakter');
    }
    if (!form.pelaporan || form.pelaporan.trim().length < 5) {
      errors.push('Pelaporan minimal 5 karakter');
    }
    if (!form.pic_unit_id || Number(form.pic_unit_id) <= 0) {
      errors.push('Unit PIC WAJIB dipilih');
    }
    if (!form.pic_user_id || Number(form.pic_user_id) <= 0) {
      errors.push('Penanggung Jawab (PIC) WAJIB dipilih');
    }
    if (!form.target_date) {
      errors.push('Target tanggal WAJIB diisi');
    }

    if (errors.length > 0) {
      toast.error(
        <div>
          <p className="font-bold mb-1">Validasi gagal:</p>
          <ul className="list-disc list-inside text-xs">
            {errors.map((err, i) => <li key={i}>{err}</li>)}
          </ul>
        </div>,
        { duration: 5000 }
      );
      return;
    }

    setSaving(true);
    try {
      // ✅ FIXED: Correct Type Conversions
      const payload = {
        rapat_id: Number(id),
        laporan_masalah_id: form.laporan_masalah_id!,
        program: form.program.trim(),
        pengendalian: form.pengendalian.trim(),
        evaluasi: form.evaluasi.trim(),
        pengembangan: form.pengembangan.trim(),
        pelaporan: form.pelaporan.trim(),
        pic_unit_id: Number(form.pic_unit_id),
        pic_user_id: Number(form.pic_user_id), // ✅ Wajib Number
        target_date: form.target_date,
        budget: Number(form.budget.replace(/\D/g, '')) || 0,
        // Priority defaults to 'sedang' if not set, or we can add priority to this form too
        priority: 'sedang',
      };

      if (editingId) {
        await rapimService.updateKeputusan(editingId, payload as any);
        toast.success('Keputusan berhasil diperbarui');
      } else {
        if (!form.laporan_masalah_id) {
          toast.error("Laporan masalah harus dipilih");
          setSaving(false);
          return;
        }
        await rapimService.createKeputusan(payload as any);
        toast.success('Keputusan PPEPP berhasil disimpan');
      }
      setShowModal(false);
      fetchData();
    } catch (e: any) {
      console.error(e);
      const errorMsg = e?.response?.data?.message || 'Gagal menyimpan keputusan';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (keputusanId: number) => {
    if (window.confirm('Yakin ingin menghapus keputusan ini?')) {
      try {
        await rapimService.deleteKeputusan(keputusanId);
        toast.success('Keputusan berhasil dihapus');
        fetchData();
      } catch (e) {
        toast.error('Gagal menghapus keputusan');
      }
    }
  };

  const formatCurrency = (value: number | undefined | null) => {
    if (value === undefined || value === null) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(value);
  };

  const formatRupiahInput = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-main" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'RAPIM', href: '/rapim' },
        { label: 'Detail Rapat', href: `/rapim/${id}` },
        { label: 'Keputusan PPEPP' },
      ]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Keputusan RAPIM (PPEPP)</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            {keputusan.length} keputusan
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            icon={<Download className="w-4 h-4" />}
            onClick={async () => {
              if (!id) return;
              try {
                const arrayBuffer = await rapimService.exportPDF(Number(id), 'hasil');
                const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `KEPUTUSAN-RAPIM-${id}.pdf`);
                document.body.appendChild(link);
                link.click();

                // Cleanup
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              } catch (e) {
                console.error('Export PDF Error:', e);
                toast.error('Gagal export PDF');
              }
            }}
          >
            Export PDF
          </Button>
          {/* Create button might be disabled here if we want users to create from Detail/Agenda view to link report */}
          {/* <Button variant="primary" size="sm" onClick={() => openCreate()} icon={<Plus className="w-4 h-4" />}>
            Tambah Keputusan
          </Button> */}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card padding="sm">
          <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Total</p>
          <p className="text-2xl font-black text-teal-main mt-1">{keputusan.length}</p>
        </Card>
        <Card padding="sm">
          <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Dalam Proses</p>
          <p className="text-2xl font-black text-warning mt-1">
            {keputusan.filter((k) => k.status === 'berlangsung').length}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Rata-rata Progress</p>
          <p className="text-2xl font-black text-teal-light mt-1">
            {keputusan.length > 0 ? Math.round(keputusan.reduce((s, k) => s + k.progress_percentage, 0) / keputusan.length) : 0}%
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Total Anggaran</p>
          <p className="text-lg font-black text-neutral-text-primary mt-1">
            {formatCurrency(keputusan.reduce((s, k) => s + (k.budget || 0), 0))}
          </p>
        </Card>
      </div>

      {/* Keputusan List */}
      <div className="space-y-4">
        {keputusan.length === 0 && <p className="text-center text-neutral-text-secondary py-8">Belum ada keputusan.</p>}
        {keputusan.map((k) => (
          <Card key={k.id} className="overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-teal-main">{k.laporan?.kode}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${k.status === 'berlangsung' ? 'bg-warning/10 text-warning'
                    : k.status === 'menunggu' ? 'bg-gray-100 text-gray-600'
                      : 'bg-success/10 text-success'
                    }`}>
                    {k.status === 'berlangsung' ? 'Berlangsung' : k.status === 'menunggu' ? 'Menunggu' : 'Selesai'}
                  </span>
                </div>
                <h3 className="text-base font-bold text-neutral-text-primary">{k.laporan?.judul}</h3>
                <div className="flex items-center gap-4 mt-2 text-xs text-neutral-text-secondary">
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {k.pic_user?.name || k.pic_unit?.name || '-'}</span>
                  <span className="flex items-center gap-1"><Target className="w-3 h-3" /> {new Date(k.target_date).toLocaleDateString('id-ID')}</span>
                  <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {formatCurrency(k.budget)}</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(k)} className="p-1.5 rounded-lg hover:bg-neutral-bg">
                  <Edit className="w-4 h-4 text-neutral-text-secondary" />
                </button>
                <button onClick={() => handleDelete(k.id)} className="p-1.5 rounded-lg hover:bg-danger/10">
                  <Trash2 className="w-4 h-4 text-danger" />
                </button>
              </div>
            </div>

            {/* Progress */}
            <div className="mt-4">
              <ProgressBar value={k.progress_percentage} />
            </div>

            {/* Expandable PPEPP Detail */}
            <button
              onClick={() => setExpandedId(expandedId === k.id ? null : k.id)}
              className="mt-4 text-xs font-semibold text-teal-main hover:underline"
            >
              {expandedId === k.id ? '▲ Sembunyikan Detail PPEPP' : '▼ Lihat Detail PPEPP'}
            </button>

            {expandedId === k.id && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-4 bg-neutral-bg rounded-xl">
                {[
                  { label: 'Program', value: k.program, color: 'border-l-teal-main' },
                  { label: 'Pengendalian', value: k.pengendalian, color: 'border-l-info' },
                  { label: 'Evaluasi', value: k.evaluasi, color: 'border-l-warning' },
                  { label: 'Pengembangan', value: k.pengembangan, color: 'border-l-success' },
                  { label: 'Pelaporan', value: k.pelaporan, color: 'border-l-purple-500' },
                ].map((item, i) => (
                  <div key={i} className={`border-l-4 ${item.color} pl-3`}>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary mb-1">
                      {item.label}
                    </p>
                    <p className="text-xs text-neutral-text-primary leading-relaxed">{item.value}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Modal PPEPP Form — FIXED dengan visual warning */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? '✏️ Edit Keputusan PPEPP' : '📋 Buat Keputusan PPEPP'}
        size="xl"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowModal(false)}>Batal</Button>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={saving}
              disabled={!form.pic_unit_id || !form.pic_user_id || !form.target_date || !form.program}
            >
              {editingId ? 'Perbarui' : 'Simpan'}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {/* Info banner */}
          <div className="p-3 bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl border border-teal-200/60">
            <p className="text-xs font-bold text-teal-700 flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" />
              Framework PPEPP (Program, Pengendalian, Evaluasi, Pengembangan, Pelaporan)
            </p>
          </div>

          {/* ✅ NEW: Warning Box jika data penting belum diisi */}
          {(!form.pic_unit_id || !form.target_date || !form.pic_user_id) && (
            <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl animate-pulse">
              <div className="flex items-start gap-3">
                <Target className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-800">
                    ⚠️ Perhatian: Data Wajib Belum Lengkap
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-amber-700">
                    {!form.pic_unit_id && (
                      <li className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                        <strong>Unit PIC</strong> wajib dipilih
                      </li>
                    )}
                    {!form.pic_user_id && (
                      <li className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                        <strong>Penanggung Jawab (PIC)</strong> wajib dipilih
                      </li>
                    )}
                    {!form.target_date && (
                      <li className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                        <strong>Target Tanggal</strong> wajib diisi
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* PPEPP Textareas */}
          <div className="space-y-4">
            <div className="border-l-4 border-l-teal-main pl-4">
              <Textarea label="Program *" placeholder="Program yang direkomendasikan..." value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} required rows={3} />
            </div>
            <div className="border-l-4 border-l-blue-500 pl-4">
              <Textarea label="Pengendalian *" placeholder="Cara pengendalian pelaksanaan..." value={form.pengendalian} onChange={(e) => setForm({ ...form, pengendalian: e.target.value })} required rows={3} />
            </div>
            <div className="border-l-4 border-l-amber-500 pl-4">
              <Textarea label="Evaluasi *" placeholder="Metode evaluasi..." value={form.evaluasi} onChange={(e) => setForm({ ...form, evaluasi: e.target.value })} required rows={3} />
            </div>
            <div className="border-l-4 border-l-emerald-500 pl-4">
              <Textarea label="Pengembangan *" placeholder="Rencana pengembangan..." value={form.pengembangan} onChange={(e) => setForm({ ...form, pengembangan: e.target.value })} required rows={3} />
            </div>
            <div className="border-l-4 border-l-purple-500 pl-4">
              <Textarea label="Pelaporan *" placeholder="Mekanisme pelaporan..." value={form.pelaporan} onChange={(e) => setForm({ ...form, pelaporan: e.target.value })} required rows={3} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-neutral-border">
            {/* ✅ Unit PIC - WAJIB */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Unit PIC <span className="text-danger">*</span>
              </label>
              <select
                className={`
                  w-full rounded-lg px-4 py-2.5 text-sm bg-white border
                  focus:outline-none focus:ring-2 transition-all
                  ${!form.pic_unit_id
                    ? 'border-amber-400 bg-amber-50 focus:ring-amber-300'
                    : 'border-neutral-border focus:ring-teal-light'
                  }
                `}
                value={form.pic_unit_id}
                onChange={(e) => setForm({ ...form, pic_unit_id: e.target.value, pic_user_id: '' })}
              >
                <option value="">⚠️ Pilih unit penanggung jawab...</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id.toString()}>{u.name}</option>
                ))}
              </select>
              {!form.pic_unit_id && (
                <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Wajib untuk penugasan
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Target Tanggal <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className={`
                  w-full rounded-lg px-4 py-2.5 text-sm bg-white border
                  focus:outline-none focus:ring-2 transition-all
                  ${!form.target_date ? 'border-amber-400 bg-amber-50' : 'border-neutral-border'}
                `}
                value={form.target_date}
                onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* ✅ PIC User - WAJIB */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Penanggung Jawab (PIC) <span className="text-danger">*</span>
              </label>
              <select
                className={`
                  w-full rounded-lg px-4 py-2.5 text-sm bg-white border
                  focus:outline-none focus:ring-2 transition-all
                  ${!form.pic_user_id
                    ? 'border-amber-400 bg-amber-50 focus:ring-amber-300'
                    : 'border-neutral-border focus:ring-teal-light'
                  }
                `}
                value={form.pic_user_id}
                onChange={(e) => setForm({ ...form, pic_user_id: e.target.value })}
              >
                <option value="">⚠️ Pilih penanggung jawab...</option>
                {users.filter(u => u.unit?.id === Number(form.pic_unit_id)).map((u) => (
                  <option key={u.id} value={u.id.toString()}>{u.name}</option>
                ))}
              </select>
              {!form.pic_user_id && (
                <p className="text-xs text-amber-600 font-medium flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Wajib untuk notifikasi
                </p>
              )}
            </div>
            <Input
              label="Estimasi Anggaran (Rp)"
              type="text"
              inputMode="numeric"
              placeholder="0"
              value={formatRupiahInput(form.budget)}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '');
                setForm({ ...form, budget: digits });
              }}
            />
          </div>

          {/* ✅ NEW: Summary sebelum submit */}
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
            <p className="text-xs font-bold text-gray-700 mb-2">📋 Ringkasan Decision</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-500">Unit:</span>
                <span className={`ml-1 font-semibold ${form.pic_unit_id ? 'text-teal-600' : 'text-danger'}`}>
                  {form.pic_unit_id ? units.find(u => u.id.toString() === form.pic_unit_id)?.name : '❌ Belum pilih'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">PIC:</span>
                <span className={`ml-1 font-semibold ${form.pic_user_id ? 'text-teal-600' : 'text-danger'}`}>
                  {form.pic_user_id ? users.find(u => u.id.toString() === form.pic_user_id)?.name : '❌ Belum pilih'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Target:</span>
                <span className={`ml-1 font-semibold ${form.target_date ? 'text-teal-600' : 'text-danger'}`}>
                  {form.target_date ? new Date(form.target_date).toLocaleDateString('id-ID') : '❌ Kosong'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RapimKeputusan;
